import type { CartItem, PaymentMethodType } from '../types'
import { formatCurrency } from '../lib/utils'
import { useCartStore } from '../stores/cartStore'

interface OrderSummaryProps {
  items: CartItem[]
  paymentMethod?: PaymentMethodType
  paymentFee?: number
  grandTotal?: number
}

function OrderSummary({ items, paymentMethod, paymentFee, grandTotal }: OrderSummaryProps) {
  const getCartTotals = useCartStore((state) => state.getCartTotals)
  const totals = getCartTotals()

  // Use grandTotal if provided (includes payment fee), otherwise use cart total
  const finalTotal = grandTotal !== undefined ? grandTotal : totals.total

  return (
    <div className="card p-6 space-y-4 sticky top-4">
      <h2 className="text-xl font-bold">Ringkasan Pesanan</h2>

      <div className="space-y-3 border-b border-gray-200 pb-4">
        {items.map((item) => (
          <div key={item.product.id} className="flex gap-3">
            <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
              {item.product.imageUrl ? (
                <img
                  src={item.product.imageUrl}
                  alt={item.product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm truncate">{item.product.name}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {item.quantity} x {formatCurrency(item.product.price)}
              </p>
              <p className="text-sm font-semibold text-primary font-mono mt-1">
                {formatCurrency(item.product.price * item.quantity)}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Subtotal</span>
          <span className="font-mono font-semibold">{formatCurrency(totals.subtotal)}</span>
        </div>
        {totals.taxAmount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Pajak</span>
            <span className="font-mono font-semibold">{formatCurrency(totals.taxAmount)}</span>
          </div>
        )}
        {totals.shippingCost > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Ongkir</span>
            <span className="font-mono font-semibold">{formatCurrency(totals.shippingCost)}</span>
          </div>
        )}
        
        {/* Payment fee - shown when payment method is selected */}
        {paymentMethod && paymentFee !== undefined && paymentFee > 0 && (
          <div className="flex justify-between text-sm text-orange-600">
            <span>Biaya Layanan Pembayaran</span>
            <span className="font-mono font-semibold">+ {formatCurrency(paymentFee)}</span>
          </div>
        )}
      </div>

      <div className="flex justify-between text-lg font-bold pt-4 border-t border-gray-200">
        <span>Total Bayar</span>
        <span className="font-mono text-primary">{formatCurrency(finalTotal)}</span>
      </div>
      
      {/* Payment fee notice */}
      {paymentMethod && paymentFee !== undefined && paymentFee > 0 && (
        <p className="text-xs text-gray-500 mt-2">
          * Biaya layanan pembayaran dibebankan oleh penyedia layanan sesuai regulasi Bank Indonesia
        </p>
      )}
    </div>
  )
}

export default OrderSummary
